import React from "react";
import { useTranslation } from "react-i18next";
import { Fighter, FightResult } from "../FightSimulatorReducer";
import { getWinner, getProbabilityB } from "network/AIProtocol";

interface ResultViewProps {
  fighterA: Fighter;
  fighterB: Fighter;
  result: FightResult;
  onRetry: () => void;
  onNewMatch: () => void;
}

const ResultView: React.FC<ResultViewProps> = ({
  fighterA,
  fighterB,
  result,
  onRetry,
  onNewMatch,
}) => {
  const { t } = useTranslation();

  const winner = getWinner(result.winProbabilityA);
  const probabilityB = getProbabilityB(result.winProbabilityA);

  const getWinnerName = () => {
    if (winner === "A") return fighterA.name;
    if (winner === "B") return fighterB.name;
    return t("FightSimulator.draw");
  };

  const getWinnerColor = () => {
    if (winner === "A") return "text-red-400";
    if (winner === "B") return "text-blue-400";
    return "text-yellow-400";
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      {/* 결과 헤더 */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-2">
          🏆 {t("FightSimulator.result")}
        </h2>
        <p className={`text-2xl font-bold ${getWinnerColor()}`}>
          {t("FightSimulator.predictedWinner")}: {getWinnerName()}
        </p>
      </div>

      {/* 승률 바 */}
      <div className="bg-gray-800 rounded-xl p-4">
        {/* Fighter A */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-red-400 font-medium">{fighterA.name}</span>
            <span className="text-red-400 font-bold">
              {result.winProbabilityA}%
            </span>
          </div>
          <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-1000"
              style={{ width: `${result.winProbabilityA}%` }}
            />
          </div>
        </div>

        {/* Fighter B */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-400 font-medium">{fighterB.name}</span>
            <span className="text-blue-400 font-bold">{probabilityB}%</span>
          </div>
          <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-1000"
              style={{ width: `${probabilityB}%` }}
            />
          </div>
        </div>
      </div>

      {/* 싸움 스토리 */}
      <div className="bg-gray-800 rounded-xl p-4">
        <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <span>📖</span>
          {t("FightSimulator.fightStory")}
        </h3>
        <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
          {result.story}
        </p>
      </div>

      {/* 주요 승부 요인 */}
      {result.keyFactors && result.keyFactors.length > 0 && (
        <div className="bg-gray-800 rounded-xl p-4">
          <h3 className="text-lg font-bold text-white mb-3">
            {t("FightSimulator.keyFactors")}
          </h3>
          <ul className="space-y-2">
            {result.keyFactors.map((factor, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-gray-300"
              >
                <span className="text-yellow-400">•</span>
                {factor}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 버튼 */}
      <div className="flex gap-4">
        <button
          onClick={onRetry}
          className="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
        >
          <i className="fas fa-redo" />
          {t("FightSimulator.retry")}
        </button>
        <button
          onClick={onNewMatch}
          className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
        >
          <i className="fas fa-plus" />
          {t("FightSimulator.newMatch")}
        </button>
      </div>
    </div>
  );
};

export default ResultView;
